import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTooltip, RCCButton, RCCTextArea, RCCPopover, RCCIcon,RCCIIconTypes, RCCSelectOptionsCard } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Audit from "src/components/Audit";
import Tabs from "src/components/Tabs";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import Select from "src/components/Select";
import ModalSide from "src/components/Modal/styles";
import LovDataOrigin from "src/components/Lov/DataOrigin";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import LovDecimalPlaces from "src/components/Lov/DecimalPlaces";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { IRefModalFormulaAssistant } from "src/components/ModalFormulaAssistant/interface";
import ModalFormulaAssistant from "src/components/ModalFormulaAssistant";
import { createPortal } from "react-dom";

import { IField, IComponentIndicatorFieldModals, IRefIndicatorFieldModals, IIndicatorFieldForm, IndicatorFieldType, WebServiceReturn } from "./interface";
import addAndChangeSchema from "./schemas";

const ModalFields: React.ForwardRefRenderFunction<IRefIndicatorFieldModals, IComponentIndicatorFieldModals> = ({
	paginationRef,
	onClose
}, ref) => {
	const { translate,currentLanguage} = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const { setLoading } = useLoaderBackdrop();

	const [selectedIndicator, setSelectedIndicator] = useState<number | undefined>(undefined);

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));

	const actionButtonDelete = (row: IField) => {
		confirm(
			translate("Exclusão do campo de indicador"),
			`${translate("Deseja realmente excluir campo")}: ${row.iqb001Descricao}(${row.iqb001Codigo}) ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "IndicatorsFields/Delete",
					urlParameters: {
						id: row.iqb001Id
					}
				});
	
				addToast({
					color: "green",
					description: translate("Campo de indicador excluído com sucesso"),
					title: translate("Sucesso")
				});

				paginationRef?.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir campo"), addToast);
			},
			true
		);
	};
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const [isCopy, setIsCopy] = useState(false);
	const [modalTitle,setModalTitle] = useState<string>("");
	const [modalIcon,setModalIcon] = useState("");
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const onCallback = useRef<Function>(() => {});
	const [selectedFieldForm, setSelectedFieldForm] = useState<IIndicatorFieldForm>({} as IIndicatorFieldForm);

	const refModalFormulaAssistant = useRef<IRefModalFormulaAssistant>(null);

	const { 
		handleSubmit : handleSubmitAddOrChange, 
		register : registerAddOrChange,
		control:controlAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		setValue:setValueAddOrChange,
		watch,
		formState, 
		clearErrors: clearErrorsAddOrChange
	} = useForm<IIndicatorFieldForm>();
	
	const { 
		fields: origins, 
		remove,
		append
	} = useFieldArray({
		control: controlAddOrChange,
		name: "iqtbb002"
	});

	const { errors : errorsAddOrChange } = formState;

	const watchFieldArray = watch("iqtbb002");

	const controlledorigins = origins.map((field, index) => ({
		...field,
		...watchFieldArray && watchFieldArray[index]
	}));

	const submitAddOrChange = async (data: any) => {
		try{
			const dataForm = data as IIndicatorFieldForm;
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();

			if(dataForm.iqb001Descricao === selectedFieldForm.iqb001Descricao && isCopy){
				dataForm.iqb001Descricao = `(${translate("CÓPIA")}) ${dataForm.iqb001Descricao.length > 590 ? dataForm.iqb001Descricao.substring(0,590):dataForm.iqb001Descricao}`;
			}

			await addAndChangeSchema(translate).validate(dataForm, {
				abortEarly: false
			});
			
			let controllerData;
			if(isAdd||isCopy){
				controllerData = await RCCServicesApi.postRequest({
					method: "IndicatorsFields/Insert",
					body: {
						...dataForm,
						iqb001Id: selectedFieldForm.iqb001Id
					}
				});

				onCallback.current(controllerData.data);

			}else{
				await RCCServicesApi.putRequest({
					method: "IndicatorsFields/Update",
					body: {
						...dataForm,
						iqb001Id:selectedFieldForm.iqb001Id
					}
				});

				onCallback.current({
					...dataForm,
					iqb001Id:selectedFieldForm.iqb001Id
				});
			}
			
			addToast({
				color: "green",
				description: isAdd||isCopy ? translate("Campo de indicador adicionado com sucesso") : translate("Campo de indicador alterado com sucesso"),
				title: translate("Sucesso")
			});
			
			if(onClose){
				onClose({
					...dataForm,
					iqb001Id:!isAdd||!isCopy ? selectedFieldForm.iqb001Id : controllerData?.data as number
				});
			}
			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef?.current?.reload();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd||isCopy ? translate("Erro ao adicionar campo") : translate("Erro ao alterar campo"), addToast);
		}
	};

	const requestField = async(fieldId:number) => {
		
		const dataController = await RCCServicesApi.getRequest({
			method: "IndicatorsFields/Select",
			urlParameters: {
				iqb001Id:fieldId
			}
		});
		setSelectedFieldForm(dataController.data);
		append(dataController.data.iqtbb002);
		
	};

	const actionButtonAdd = (callback?: Function) => {
		onCallback.current = callback ?? (() => {});
		setSelectedIndicator(undefined);
		resetAddOrChange(); 
		setSelectedFieldForm({} as IIndicatorFieldForm); 
		setIsAdd(true); 
		setModalTitle(translate("Adicionar campo de indicador"));
		setModalIcon("plus");
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = async(row: IField, callback?: Function, indicator?: number,copy?:boolean) => {
		try {
			setSelectedIndicator(indicator);
			setLoading(true);
			resetAddOrChange(); 
			setIsAdd(false); 
			setIsCopy(copy||false);
			setModalTitle(copy ? translate("Copiar campo de indicador") : translate("Alterar campo de indicador"));
			setModalIcon(copy ? "copy": "pencil");
			await requestField(row.iqb001Id);
			onCallback.current = callback ?? (() => {});
			setLoading(false);
			modalAddOrChange.current?.openModal(); 
		} catch (error) {
			setLoading(false);
			standardErrorMessages(error, translate("Erro ao selecionar campo"), addToast);
		}
	};

	useEffect(() => {
		if(errorsAddOrChange.iqtbb002){
			errorsAddOrChange.iqtbb002.forEach((value,index) => {
				if((value?.iqb002Formula || value?.iqb002Descricao || value?.iqb002Efa020Id || value?.iqb002Wstiporet || value?.iqb002Wspathret) && !controlledorigins[index].showFormula){
					setValueAddOrChange(`iqtbb002.${index}.showFormula`, true);
				}
			});
		}
	}, [formState]);

	return (
		<>
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: modalTitle,
					subtitle:selectedFieldForm.iqb001Descricao ? `${translate("Campo")}:${selectedFieldForm.iqb001Descricao}`:"",
					icon: {
						name: modalIcon as RCCIIconTypes
					}
				}}
				withoutForm
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<Tabs
					name="fieldsTab"
					tabInit={0}
					options={[{
						label: translate("Dados"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
									justifyContent="flex-end"
								>
									<RCCGrid item>
										<Audit data={selectedFieldForm} type="pop" position="left"/> 
									</RCCGrid>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCInput 
										register={registerAddOrChange}
										name="iqb001Descricao"
										label={translate("Descrição")}
										maxLength={600}
										defaultValue={selectedFieldForm.iqb001Descricao}
										error={errorsAddOrChange.iqb001Descricao}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCInput 
										register={registerAddOrChange}
										name="iqb001Label"
										label={translate("Rótulo")}
										maxLength={200}
										defaultValue={selectedFieldForm.iqb001Label}
										error={errorsAddOrChange.iqb001Label}
										required
									/>
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<LovDecimalPlaces
										control={controlAddOrChange}
										name="iqb001Casasdecimais"
										required
										defaultValue={selectedFieldForm?.iqb001Casasdecimais?.toString()}
										error={errorsAddOrChange.iqb001Casasdecimais}
									/>
									
								</RCCGrid>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCInput 
										register={registerAddOrChange}
										name="iqb001Codigo"
										label={translate("Código")}
										maxLength={20}
										defaultValue={isCopy ? "" : selectedFieldForm.iqb001Codigo}
										error={errorsAddOrChange.iqb001Codigo}
									/>
								</RCCGrid>
							</RCCGrid>
						)
					},
					{
						label: translate("Origens"),
						content: (
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<RCCGrid container>
										<RCCGrid container justifyContent="space-between" alignContent="flex-end" wrap="nowrap">
											<RCCGrid container item justifyContent="flex-end">
												<RCCTooltip
													text={translate("Adicionar origem")}
													color="purple"
													position="left">
													<RCCButton
														icon={{name: "plus"}}
														color="purple"
														onClick={() => {append({iqb002Id:0,iqb002Tipo:IndicatorFieldType.FORMULA,iqb002Wstiporet:WebServiceReturn.JSON});}}
														layout="isRounded"
														size="sm"
													/>	
												</RCCTooltip>
											</RCCGrid>
										</RCCGrid>
										{controlledorigins && controlledorigins.map((origin,index) => 
										 (
												<RCCGrid 
													key={origin.id}
													container
													item
													styleInline="padding-top:10px;">
													<RCCGrid
														container
														item
														spacing={1}
														alignContent="center"
														justify="center">
														<RCCGrid
															container
															wrap="nowrap"
														>
															<RCCGrid container item >
																<DateTimePickerRange
																	control={controlAddOrChange}
																	nameInit = {`iqtbb002.${index}.iqb002Dtvalini`}
																	nameEnd = {`iqtbb002.${index}.iqb002Dtvalfim`}
																	label = {translate("Validade")}
																	labelSeparator={translate("Até")}
																	type = "datePicker"
																	required
																	defaultValueInit={origin.iqb002Dtvalini ? origin.iqb002Dtvalini : new Date(new Date().getFullYear(), 0, 1)}
																	defaultValueEnd={origin.iqb002Dtvalfim ? origin.iqb002Dtvalfim : new Date(new Date().getFullYear(), 11, 31)}
																	error={errorsAddOrChange.iqtbb002 && (errorsAddOrChange.iqtbb002[index]?.iqb002Dtvalini||errorsAddOrChange.iqtbb002[index]?.iqb002Dtvalfim)}	
																/>
															</RCCGrid>
															<RCCGrid  
																item 
																container
																styleInline="align-self: center;"
																xs={1}
																justifyContent="flex-end" 
																alignContent="flex-end"
															> 
																<RCCTooltip
																	text={translate("Excluir origem")}
																	color="red"
																	position="left">
																	<RCCButton
																		icon={{name: "trash"}}
																		color="red"
																		onClick={() => {remove(index);}}
																		layout="isRounded"
																		size="sm"
																	/>	
																</RCCTooltip>
															</RCCGrid>
														</RCCGrid>
														<RCCGrid
															container
															wrap="nowrap"
														>
															<RCCGrid container item >
																<RCCSelectOptionsCard
																	name={`iqtbb002[${index}].iqb002Tipo`}
																	control={controlAddOrChange}
																	defaultValue={origin.iqb002Tipo}
																	options={[
																		{
																			value: IndicatorFieldType.FORMULA,
																			label: translate("Fórmula"),
																			icon: <RCCIcon name="xSquare"/>
																		},
																		{
																			value: IndicatorFieldType.INTEGRATION,
																			label: translate("Banco de Dados"),
																			icon: <RCCIcon name="database"/>
																		},
																		{
																			value: IndicatorFieldType.WEBSERVICE,
																			label: translate("Web Service"),
																			icon: <RCCIcon name="cloud"/>
																		}
																	]}
																	label={`${translate("Qual a origem dos dados")}?`}
																/>

															</RCCGrid>

														</RCCGrid>
									
														<RCCGrid container>
															<RCCGrid container wrap="nowrap" alignItems="center">
																<RCCGrid
																	container
																	wrap="nowrap"
																>
																	<RCCGrid 
																		container
																		wrap="nowrap"
																	>
																		<RCCGrid container item>
																			<RCCTextArea 
																				register={registerAddOrChange}
																				name={`iqtbb002.${index}.iqb002Formula`}
																				label={translate("Fórmula ou integração")}
																				defaultValue={origin.iqb002Formula}
																				error={errorsAddOrChange.iqtbb002 && errorsAddOrChange.iqtbb002[index]?.iqb002Formula}
																				required = {origin.iqb002Tipo === IndicatorFieldType.FORMULA}
																			/>
																		</RCCGrid>
																		<RCCGrid 
																			container 
																			item 
																			direction="column" 
																			justifyContent="center" 
																			alignContent="center"
																			xs={1}
																		>
																			<RCCPopover
																				header= {{
																					title: translate("O quê devo informar para Fórmula")
																				}}
																				body= {{
																					custom:
																		<div className="popover-body"> 
																			{translate("Crie uma fórmula utilizando nosso assistente (apenas clique no botão x²) ou informe um condicional SQL")}.<b> {translate("Se você for informar o valor, deixe o campo vazio")}.</b> {currentLanguage === "pt" && "Procure utilizar . como separador decimal numérico."}
																		</div>
																				}}
																			>
																				<RCCIcon
																					name="infoCircle"/>
																			</RCCPopover>
																			<RCCPopover
																				header= {{
																					title: translate("O quê devo informar para Integração")
																				}}
																				body= {{
																					custom:
																		<div className="popover-body">
																			{translate("Informe uma consulta SQL caso queira buscar o valor de outros sistemas")}. {translate("Você pode utilizar os binds")} <b>:DTINI</b> ({translate("data inicial")}), <b>:DTFIM</b> ({translate("data final")}) {translate("ou")} <b>:CODPERIODO</b> ({translate("código do período")}).<b> {translate("Se você for informar o valor, deixe o campo vazio")}.</b>
																		</div>
																				}}
																			>
																				<RCCIcon
																					name="infoCircle"/>
																			</RCCPopover>
																		</RCCGrid>
																
																	</RCCGrid>

																	{(origin.iqb002Tipo === IndicatorFieldType.FORMULA) && 
																		<RCCGrid  
																			item 
																			styleInline="align-self: center;"
																		>
																			<RCCTooltip
																				text={translate("Abrir assistente de fórmulas")}
																				color="grey"
																				position="left">

																				<RCCButton
																					icon={{name: "xSquare"}}
																					color="grey"
																					onClick={() => {
																						refModalFormulaAssistant.current?.openModal(selectedIndicator ?? 0, origin.iqb002Formula, (value: string, textValue: string) => {
																							setValueAddOrChange(`iqtbb002.${index}.iqb002Formula`, value);
																							setValueAddOrChange(`iqtbb002.${index}.iqb002Descricao`, textValue);
																						});
																					}}
																					layout="isRounded"
																					size="sm"
																				/>
																			</RCCTooltip>
																		</RCCGrid>
																	}

																</RCCGrid>
															</RCCGrid>

															<RCCGrid 
																container 
																wrap="nowrap" 
																alignItems="center"
															>
																<RCCGrid container item>
																	<RCCTextArea 
																		register={registerAddOrChange}
																		name={`iqtbb002.${index}.iqb002Descricao`}
																		label={translate("Descrição")}
																		maxLength={4000}
																		defaultValue={origin.iqb002Descricao}
																		error={errorsAddOrChange.iqtbb002 && errorsAddOrChange.iqtbb002[index]?.iqb002Descricao}
																	/>
																</RCCGrid>
																
																<RCCGrid 
																	container 
																	item
																	direction="column" 
																	justifyContent="center" 
																	alignContent="center"
																	xs={1}
																>
																	<RCCPopover
																		header= {{
																			title: translate("O quê devo informar para Descrição")
																		}}
																		body= {{
																			custom:
																				<div className="popover-body">
																					{translate("Informe uma descrição para sua fórmula/integração")}.<b>{translate("Isso ajudará futura manutenção")}.</b> {translate("Explique a lógica e o resultado esperado")}.
																				</div>
																		}}
																	>
																		<RCCIcon
																			name="infoCircle"/>
																	</RCCPopover>
																</RCCGrid>	
															</RCCGrid>
															{(origin.iqb002Tipo === IndicatorFieldType.INTEGRATION||origin.iqb002Tipo === IndicatorFieldType.WEBSERVICE) && 
																<RCCGrid container>
																	<LovDataOrigin
																		control={controlAddOrChange}
																		name={`iqtbb002.${index}.iqb002Efa020Id`}
																		label={translate("Origem de dados para integração")}
																		defaultValue={origin.iqb002Efa020Id?.toString()}
																		error={errorsAddOrChange.iqtbb002 && errorsAddOrChange.iqtbb002[index]?.iqb002Efa020Id}
																		required
																	/>
																</RCCGrid>
															}
															{(origin.iqb002Tipo === IndicatorFieldType.WEBSERVICE) && 
																<RCCGrid container spacing={1}>
																	<RCCGrid container item xs={12} md={5}> 
																		<Select
																			control={controlAddOrChange}
																			name={`iqtbb002[${index}].iqb002Wstiporet`}
																			label={`${translate("Qual o formato dos dados")}?`}
																			required
																			defaultValue={origin.iqb002Wstiporet}
																			error={errorsAddOrChange.iqtbb002 && errorsAddOrChange.iqtbb002[index]?.iqb002Wstiporet}
																			options={[
																				{value:WebServiceReturn.JSON,label:"JSON"},
																				{value:WebServiceReturn.XML,label:"XML"}
																			]}
																		/>
																	</RCCGrid>
																	<RCCGrid container item xs={12} md={7}>
																		<RCCTextArea
																			styleInline="padding-top: 22px; margin-top: 1px;"
																			register={registerAddOrChange}
																			name={`iqtbb002.${index}.iqb002Wspathret`}
																			label={translate("Caminho de origem")}
																			maxLength={2000}
																			defaultValue={origin.iqb002Wspathret}
																			error={errorsAddOrChange.iqtbb002 && errorsAddOrChange.iqtbb002[index]?.iqb002Wspathret}
																		/>
																	</RCCGrid>
																</RCCGrid>
															}
														</RCCGrid>
												
													</RCCGrid>
													<hr style={{marginTop:"20px",marginBottom:"20px"}}/>
												</RCCGrid>
											)
										)}
									</RCCGrid>
								</RCCGrid>
							</RCCGrid>
						)
					}
					]}
				/>
			</Modal>


			{createPortal(
				<ModalFormulaAssistant 
					ref={refModalFormulaAssistant}
				/>,
				document.body
			)}

			
		</>
	);
};

export default forwardRef(ModalFields);

